import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import { StoreLocatorEmptyStates } from 'features/store/components/store-locator-empty-states';
import { IStore, mapStoreResponse } from 'features/store/pages/pickup-store-selector/ducks/stores';
import isEqual from 'lodash-es/isEqual';
import { FlatList, View } from 'react-native';

import { IRestaurant } from '@rbi-ctg/store';
import { ClickEventComponentNames, CustomEventNames, logButtonClick } from 'state/amplitude';
import { useGeolocation } from 'state/geolocation';
import { t } from 'state/intl/translations';

import { LoadingIndicator } from '../loading-indicator';
import { StoreCard } from '../store-card';
import { FocusedBy, StoreCardListRef, StoreLocatorActionType } from '../types';

import { IStoreCardListProps, StoreCardItemProps } from './types';

const ESTIMATED_COLLAPSED_CARD_HEIGHT = 160;

const areItemPropsEqual = (prev: StoreCardItemProps, next: StoreCardItemProps) => {
  const prevIsSelected = prev.item._id === prev.focusedId;
  const nextIsSelected = next.item._id === next.focusedId;

  if (prevIsSelected !== nextIsSelected) {
    return false;
  }

  return (
    prev.item === next.item &&
    prev.isLastItem === next.isLastItem &&
    prev.store === next.store &&
    prev.tab === next.tab &&
    prev.isInSupportForm === next.isInSupportForm
  );
};

const StoreCardItem = React.memo((props: StoreCardItemProps) => {
  const {
    item,
    focusedId,
    isLastItem,
    store,
    tab,
    onFavoriteClick,
    handleOnCardPress,
    isInSupportForm,
    onSelectStore,
    onLayoutHeight,
  } = props;

  const handleLayout = useCallback(
    (e: any) => {
      if (store?._id) {
        onLayoutHeight(store._id, e.nativeEvent.layout.height);
      }
    },
    [store?._id, onLayoutHeight]
  );

  if (!store?._id) {
    return null;
  }

  return (
    <View onLayout={handleLayout}>
      <StoreCard
        tab={tab}
        restaurant={store}
        onFavoriteClick={onFavoriteClick}
        onCardPress={handleOnCardPress}
        isSelected={item._id === focusedId}
        isInSupportForm={isInSupportForm}
        isLastItem={isLastItem}
        onSelectStore={onSelectStore}
      />
    </View>
  );
}, areItemPropsEqual);

const StoreCardList = forwardRef<StoreCardListRef, IStoreCardListProps>(
  (
    {
      currentTabHasError = false,
      isSearching,
      restaurants = [],
      tab,
      onSelectStore,
      isInSupportForm,
      restaurantFocused,
      onRestaurantHighlight,
      storesById,
      onFavoriteClick,
      actions,
    },
    ref
  ) => {
    const { coordinatesAvailable, isPermissionGranted } = useGeolocation();
    const flatListRef = useRef<FlatList | null>(null);
    const itemHeights = useRef<{ [key: string]: number }>({});
    const scrollTimerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      return () => {
        if (scrollTimerRef.current) {
          clearTimeout(scrollTimerRef.current);
        }
      };
    }, []);

    const getStoreById = useCallback(
      (storeId: string) => {
        return storesById?.[storeId];
      },
      [storesById]
    );

    const handleOnCardPress = useCallback(
      ({ restaurant }: { restaurant: IRestaurant }) => {
        actions.current[StoreLocatorActionType.USER_SELECTION] = true;
        const restaurantToIStore = mapStoreResponse(restaurant);
        onRestaurantHighlight({ restaurant: restaurantToIStore, focusedBy: FocusedBy.LIST });
        if (restaurant._id) {
          logButtonClick({
            attributes: {
              StoreId: restaurant._id,
              Name: CustomEventNames.BUTTON_CLICK_STORE_SELECTED,
              name: CustomEventNames.BUTTON_CLICK_STORE_SELECTED,
              component: ClickEventComponentNames.BUTTON,
            },
          });
        }
      },
      [actions, onRestaurantHighlight]
    );

    const scrollToTop = useCallback(() => {
      if (!flatListRef.current || !restaurants.length) {
        return;
      }

      // small delay to ensure the list is rendered
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
      scrollTimerRef.current = setTimeout(() => {
        flatListRef.current?.scrollToOffset({ animated: true, offset: 0 });
      }, 0);
    }, [restaurants]);

    const scrollToRestaurant = useCallback(
      (restaurantId: string) => {
        if (!flatListRef.current || !restaurants.length) {
          return;
        }

        const restaurantIndex = restaurants.findIndex(
          restaurant => restaurant._id === restaurantId
        );
        if (restaurantIndex !== -1) {
          // Wait for layout animations/renders to finish so itemHeights are fully updated
          // with the collapsed/expanded heights before calculating the final offset.
          if (scrollTimerRef.current) {
            clearTimeout(scrollTimerRef.current);
          }
          scrollTimerRef.current = setTimeout(() => {
            let offset = 0;
            for (let i = 0; i < restaurantIndex; i++) {
              const id = restaurants[i]?._id;
              offset +=
                (id ? itemHeights.current[id] : ESTIMATED_COLLAPSED_CARD_HEIGHT) ||
                ESTIMATED_COLLAPSED_CARD_HEIGHT;
            }

            flatListRef.current?.scrollToOffset({
              animated: true,
              offset,
            });
          }, 0);
        }
      },
      [restaurants]
    );

    useImperativeHandle(
      ref,
      () => ({
        scrollToTop,
        scrollToRestaurant,
      }),
      [scrollToRestaurant, scrollToTop]
    );

    const handleLayoutHeight = useCallback((storeId: string, height: number) => {
      itemHeights.current[storeId] = height;
    }, []);

    const renderItem = useCallback(
      ({ item, index }: { item: IStore; index: number }) => {
        const isLastItem = index === restaurants.length - 1;
        const store = item._id ? getStoreById(item._id) : null;

        return (
          <StoreCardItem
            item={item}
            focusedId={restaurantFocused?._id}
            isLastItem={isLastItem}
            store={store}
            tab={tab}
            onFavoriteClick={onFavoriteClick}
            handleOnCardPress={handleOnCardPress}
            isInSupportForm={isInSupportForm}
            onSelectStore={onSelectStore}
            onLayoutHeight={handleLayoutHeight}
          />
        );
      },
      [
        restaurants.length,
        getStoreById,
        restaurantFocused?._id,
        onSelectStore,
        isInSupportForm,
        handleOnCardPress,
        onFavoriteClick,
        tab,
        handleLayoutHeight,
      ]
    );

    if (isSearching) {
      return <LoadingIndicator ariaLabel={t('locationSearch')} />;
    }

    if (restaurants.length === 0) {
      return (
        <StoreLocatorEmptyStates
          tab={tab}
          locationPermissionsGranted={isPermissionGranted}
          coordinatesAvailable={coordinatesAvailable}
          currentTabError={currentTabHasError}
          wrapperStyle={{
            width: '100%',
          }}
        />
      );
    }

    if (restaurants.length === 0) {
      return <LoadingIndicator ariaLabel={t('locationSearch')} />;
    }

    return (
      <FlatList
        keyExtractor={(item, index) => item?._id || index + '_restaurant'}
        data={restaurants}
        extraData={restaurantFocused?._id}
        renderItem={renderItem}
        ref={flatListRef}
      />
    );
  }
);

export default React.memo(StoreCardList, (prevProps, nextProps) => {
  if (prevProps.restaurantFocused !== nextProps.restaurantFocused) {
    return false;
  }
  if (prevProps.tab !== nextProps.tab) {
    return false;
  }
  if (prevProps.isSearching !== nextProps.isSearching) {
    return false;
  }
  if (prevProps.currentTabHasError !== nextProps.currentTabHasError) {
    return false;
  }
  if (
    !isEqual(
      prevProps?.restaurants?.map(x => x._id),
      nextProps?.restaurants?.map(x => x._id)
    )
  ) {
    return false;
  }

  if (prevProps.ref !== nextProps.ref) {
    return false;
  }

  return true;
});
